import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Label,
  Modal,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import { ITEMS_PER_PAGE } from '../../constants';

const STATUS_OPTIONS = [
  { key: 'all', text: '全部', value: '' },
  { key: 'pending', text: 'pending', value: 'pending' },
  { key: 'processing', text: 'processing', value: 'processing' },
  { key: 'completed', text: 'completed', value: 'completed' },
  { key: 'failed', text: 'failed', value: 'failed' },
];

function renderStatus(status) {
  const colorMap = {
    pending: 'grey',
    processing: 'blue',
    completed: 'green',
    failed: 'red',
  };
  return (
    <Label basic color={colorMap[status] || 'grey'}>
      {status || 'unknown'}
    </Label>
  );
}

function renderQueueStage(stage) {
  const colorMap = {
    queued: 'grey',
    claimed: 'teal',
    executing: 'blue',
    completed: 'green',
    failed: 'red',
  };
  return (
    <Label basic color={colorMap[stage] || 'grey'}>
      {stage || '-'}
    </Label>
  );
}

function formatJSON(value) {
  if (!value) return '';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

const AsyncTask = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(false);
  const [filters, setFilters] = useState({
    task_id: '',
    status: '',
    token: '',
    model: '',
  });

  const loadTasks = async (page = 0) => {
    setLoading(true);
    const params = new URLSearchParams({
      p: String(page),
      task_id: filters.task_id || '',
      status: filters.status || '',
      token: filters.token || '',
      model: filters.model || '',
    });
    const res = await API.get(`/api/admin/async-tasks?${params.toString()}`);
    const { success, data, total, message } = res.data;
    if (!success) {
      showError(message);
      setLoading(false);
      return;
    }
    setItems(data || []);
    setTotal(total || 0);
    setLoading(false);
  };

  useEffect(() => {
    loadTasks(0);
  }, []);

  const openDetail = async (taskID) => {
    const res = await API.get(`/api/admin/async-tasks/${taskID}`);
    const { success, data, message } = res.data;
    if (!success) {
      showError(message);
      return;
    }
    setDetail(data);
    setDetailOpen(true);
  };

  const handleRetry = async (taskId) => {
    setRetryLoading(true);
    try {
      const res = await API.post(`/api/admin/async-tasks/${taskId}/retry`);
      const { success, message } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      showSuccess('任务已重新提交');
      if (detailOpen) {
        setDetailOpen(false);
        setDetail(null);
      }
      loadTasks(activePage - 1);
    } finally {
      setRetryLoading(false);
    }
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>异步任务</Card.Header>
          <Form>
            <Form.Group>
              <Form.Input
                label='Task ID'
                width={4}
                name='task_id'
                value={filters.task_id}
                onChange={(e, { name, value }) =>
                  setFilters({ ...filters, [name]: value })
                }
              />
              <Form.Select
                label='状态'
                width={3}
                name='status'
                options={STATUS_OPTIONS}
                value={filters.status}
                onChange={(e, { name, value }) =>
                  setFilters({ ...filters, [name]: value })
                }
              />
              <Form.Input
                label='令牌 ID/名称'
                width={3}
                name='token'
                placeholder='数字=精确ID，文字=模糊名称'
                value={filters.token}
                onChange={(e, { name, value }) =>
                  setFilters({ ...filters, [name]: value })
                }
              />
              <Form.Input
                label='模型'
                width={4}
                name='model'
                value={filters.model}
                onChange={(e, { name, value }) =>
                  setFilters({ ...filters, [name]: value })
                }
              />
              <Form.Button
                label='&nbsp;'
                width={2}
                onClick={() => {
                  setActivePage(1);
                  loadTasks(0);
                }}
              >
                查询
              </Form.Button>
            </Form.Group>
          </Form>
          <div style={{ overflowX: 'auto' }}>
            <Table
              celled
              style={{ minWidth: 'max-content', width: '100%' }}
              loading={loading ? 'true' : undefined}
            >
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Task ID</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell>Queue Stage</Table.HeaderCell>
                  <Table.HeaderCell>Channel ID</Table.HeaderCell>
                  <Table.HeaderCell>模型</Table.HeaderCell>
                  <Table.HeaderCell>Relay Mode</Table.HeaderCell>
                  <Table.HeaderCell>创建时间</Table.HeaderCell>
                  <Table.HeaderCell>更新时间</Table.HeaderCell>
                  <Table.HeaderCell>操作</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.map((item) => (
                  <Table.Row key={item.task_id}>
                    <Table.Cell>{item.task_id}</Table.Cell>
                    <Table.Cell>{renderStatus(item.status)}</Table.Cell>
                    <Table.Cell>{renderQueueStage(item.queue_stage)}</Table.Cell>
                    <Table.Cell>{item.channel_id}</Table.Cell>
                    <Table.Cell>{item.model}</Table.Cell>
                    <Table.Cell>{item.relay_mode}</Table.Cell>
                    <Table.Cell>{timestamp2string(item.created_at)}</Table.Cell>
                    <Table.Cell>{timestamp2string(item.updated_at)}</Table.Cell>
                    <Table.Cell>
                      <Button
                        size='tiny'
                        onClick={() => openDetail(item.task_id)}
                      >
                        查看详情
                      </Button>
                      {item.status === 'failed' && (
                        <Button
                          size='tiny'
                          color='orange'
                          loading={retryLoading}
                          disabled={retryLoading}
                          onClick={() => handleRetry(item.task_id)}
                        >
                          重试
                        </Button>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          {total > ITEMS_PER_PAGE && (
            <Pagination
              activePage={activePage}
              totalPages={Math.ceil(total / ITEMS_PER_PAGE)}
              onPageChange={(_, { activePage }) => {
                setActivePage(activePage);
                loadTasks(activePage - 1);
              }}
            />
          )}
        </Card.Content>
      </Card>

      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        closeIcon
        size='large'
      >
        <Modal.Header>异步任务详情</Modal.Header>
        <Modal.Content scrolling>
          {detail && (
            <Form>
              <Form.Group widths='equal'>
                <Form.Input
                  label='Task ID'
                  value={detail.task_id || ''}
                  readOnly
                />
                <Form.Input label='状态' value={detail.status || ''} readOnly />
                <Form.Input
                  label='Queue Stage'
                  value={detail.queue_stage || ''}
                  readOnly
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='User ID'
                  value={String(detail.user_id ?? '')}
                  readOnly
                />
                <Form.Input
                  label='Channel ID'
                  value={String(detail.channel_id ?? '')}
                  readOnly
                />
                <Form.Input
                  label='Token ID'
                  value={String(detail.token_id ?? '')}
                  readOnly
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='Relay Mode'
                  value={String(detail.relay_mode ?? '')}
                  readOnly
                />
                <Form.Input label='模型' value={detail.model || ''} readOnly />
                <Form.Input
                  label='重试次数'
                  value={String(detail.retry_count ?? 0)}
                  readOnly
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='Upstream Task ID'
                  value={detail.up_stream_task_id || '-'}
                  readOnly
                />
                <Form.Input
                  label='创建时间'
                  value={timestamp2string(detail.created_time)}
                  readOnly
                />
                <Form.Input
                  label='更新时间'
                  value={timestamp2string(detail.updated_time)}
                  readOnly
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='完成时间'
                  value={
                    detail.completed_time
                      ? timestamp2string(detail.completed_time)
                      : '-'
                  }
                  readOnly
                />
              </Form.Group>
              {detail.error_message && (
                <Form.TextArea
                  label='错误信息'
                  value={detail.error_message}
                  rows={3}
                />
              )}
              <Form.TextArea
                label='query_request'
                value={detail.query_request || ''}
                rows={2}
              />
              <Form.TextArea
                label='request_data'
                value={formatJSON(detail.request_data)}
                rows={6}
              />
              <Form.TextArea
                label='response_data'
                value={formatJSON(detail.response_data)}
                rows={6}
              />
              <Form.TextArea
                label='result_data'
                value={formatJSON(detail.result_data)}
                rows={4}
              />
              <Form.TextArea
                label='billing_info'
                value={formatJSON(detail.billing_info)}
                rows={4}
              />
            </Form>
          )}
        </Modal.Content>
        {detail && detail.status === 'failed' && (
          <Modal.Actions>
            <Button
              color='orange'
              loading={retryLoading}
              disabled={retryLoading}
              onClick={() => handleRetry(detail.task_id)}
            >
              重试此任务
            </Button>
          </Modal.Actions>
        )}
      </Modal>
    </div>
  );
};

export default AsyncTask;
